import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Upload, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { AsyncSearchableSelect } from '../ui/async-searchable-select';
import { SearchableSelect } from '../ui/searchable-select';
import { directoryApi, uploadsApi } from '../../services/api';
import { toast } from 'sonner';

const createEmptyDimensionRow = () => ({
  pieces: '',
  length: '',
  width: '',
  height: '',
  unit: 'cm',
  stackability: ''
});
const toInt = (value) => {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};
const getTotalPiecesFromRows = (rows = []) =>
  (Array.isArray(rows) ? rows : []).reduce((sum, row) => sum + toInt(row?.pieces), 0);
const mapAirportToOption = (airport) => ({
  value: airport.code,
  label: airport.nameAndLocode || `${airport.name} (${airport.code})`,
  country: airport.country,
});
const AIR_CARGO_TYPE_OPTIONS = [
  { value: 'General', label: 'General Cargo' },
  { value: 'Documents', label: 'Documents' },
  { value: 'Dangerous', label: 'Dangerous Goods' },
  { value: 'Perishable', label: 'Perishable' },
];
const WEIGHT_UNIT_OPTIONS = [
  { value: 'kg', label: 'kg' },
  { value: 'lbs', label: 'lbs' },
];
const DIMENSION_UNIT_OPTIONS = [
  { value: 'mm', label: 'mm' },
  { value: 'cm', label: 'cm' },
  { value: 'inch', label: 'Inch' },
  { value: 'm', label: 'Meter' },
];
const HAZ_CLASS_OPTIONS = [
  'Class 1 - Explosives',
  'Class 2 - Gases',
  'Class 3 - Flammable Liquids',
  'Class 4 - Flammable Solids',
  'Class 5 - Oxidizing Substances',
  'Class 6 - Toxic Substances',
  'Class 7 - Radioactive Material',
  'Class 8 - Corrosives',
  'Class 9 - Miscellaneous'
];
const MAX_MSDS_FILES = 5;
const MAX_MSDS_FILE_SIZE_MB = 5;
const MAX_MSDS_FILE_SIZE_BYTES = MAX_MSDS_FILE_SIZE_MB * 1024 * 1024;

const DEFAULT_AIR_FORM = {
  scope: [],
  origin: '', originCountry: '',
  destination: '', destinationCountry: '',
  commodity: '',
  cargoType: 'General',
  totalWeight: '',
  weightUnit: 'kg',
  pieces: '',
  dimensionsRows: [createEmptyDimensionRow()],
  serviceLevel: 'Standard',
  incoterm: '',
  isHazardous: false,
  hazClass: '',
  unNumber: '',
  msdsFiles: [],
  specialHandling: [],
  temperatureRange: '',
  pickupAddress: '',
  deliveryAddress: '',
  insuranceRequired: false,
  insuranceValue: '',
  customsValue: '',
  specialRequirements: ''
};

const DEFAULT_AIR_SCOPE = ['Origin Pick-up & Handling', 'Origin Clearance', 'Origin Documentation', 'Airline / Terminal Charges', 'Air Freight Rate', 'Destination Air Charges', 'Import Clearance', 'Destination Inland'];
const PICKUP_ADDRESS_SCOPES = new Set(['Origin Pick-up & Handling', 'Origin Pickup & Handling', 'Origin Pick-up', 'Origin Handling']);
const DESTINATION_ADDRESS_SCOPES = new Set(['Destination Inland']);

const AirFreightForm = ({ onSubmit, onCancel, airports = [], commodities = [], incoterms = [], initialData = null, formId = null, scopeOptions: scopeOptionsProp, onFormDataChange, validationErrors = {}, clearValidationError }) => {
  const [formData, setFormData] = useState(() => {
    if (initialData && typeof initialData === 'object') {
      const normalizedIncoterm = Array.isArray(initialData.incoterm)
        ? (initialData.incoterm[0] || '')
        : (initialData.incoterm || '');
      const legacyDimensions = initialData?.dimensions || {};
      const rawRows = Array.isArray(initialData?.dimensionsRows) && initialData.dimensionsRows.length > 0
        ? initialData.dimensionsRows
        : Array.isArray(initialData?.dimensionsList) && initialData.dimensionsList.length > 0
          ? initialData.dimensionsList.map((row) => ({ ...row, unit: row?.unit || legacyDimensions?.unit || 'cm', pieces: row?.pieces ?? '' }))
          : [{
              pieces: initialData?.pieces ?? '',
              length: legacyDimensions?.length ?? '',
              width: legacyDimensions?.width ?? '',
              height: legacyDimensions?.height ?? '',
              unit: legacyDimensions?.unit ?? 'cm',
              stackability: initialData?.stackability || ''
            }];
      const dimensionsRows = rawRows.map((row) => ({
        pieces: row?.pieces ?? '',
        length: row?.length ?? '',
        width: row?.width ?? '',
        height: row?.height ?? '',
        unit: row?.unit ?? 'cm',
        stackability: row?.stackability || ''
      }));
      return {
        ...DEFAULT_AIR_FORM,
        ...initialData,
        incoterm: normalizedIncoterm,
        dimensionsRows,
        pieces: initialData?.pieces || getTotalPiecesFromRows(dimensionsRows) || ''
      };
    }
    return { ...DEFAULT_AIR_FORM };
  });
  const [airportLabels, setAirportLabels] = useState({ origin: '', destination: '' });
  const [uploadingMsds, setUploadingMsds] = useState(false);
  const msdsInputRef = useRef(null);
  const scopeOptions = scopeOptionsProp && scopeOptionsProp.length > 0 ? scopeOptionsProp : DEFAULT_AIR_SCOPE;
  const serviceLevels = ['Express', 'Standard', 'Economy', 'Deferred'];
  const specialHandlingOptions = ['Temperature Controlled', 'Fragile', 'High Value', 'Live Animals', 'Perishable'];
  const hazClassOptions = HAZ_CLASS_OPTIONS.map((haz) => ({ value: haz, label: haz }));
  const requiresPickupAddress = formData.scope.some((scope) => PICKUP_ADDRESS_SCOPES.has(scope));
  const requiresDestinationAddress = formData.scope.some((scope) => DESTINATION_ADDRESS_SCOPES.has(scope));
  const commodityOptions = commodities.map((comm) => {
    const value = typeof comm === 'object' ? comm.name || String(comm) : comm;
    return { value, label: value };
  });
  const serviceLevelOptions = serviceLevels.map((level) => ({ value: level, label: level }));
  const incotermOptions = incoterms.map((term) => {
    const value = typeof term === 'object' ? term.code : term;
    const label = typeof term === 'object' ? `${term.code} - ${term.name}` : term;
    return { value, label };
  });

  useEffect(() => {
    const fetchLabel = (field, code) => {
      if (!code) return;
      directoryApi.getAirportByCode(code).then((res) => {
        const item = res.data;
        const label = item?.nameAndLocode || (item?.name && item?.code ? `${item.name} (${item.code})` : code);
        setAirportLabels((prev) => ({ ...prev, [field]: label }));
      }).catch(() => setAirportLabels((prev) => ({ ...prev, [field]: code })));
    };

    if (formData.origin) fetchLabel('origin', formData.origin);
    if (formData.destination) fetchLabel('destination', formData.destination);
  }, [formData.origin, formData.destination]);

  useEffect(() => {
    onFormDataChange?.(formData);
  }, [formData, onFormDataChange]);

  const handleMsdsUpload = async (event) => {
    const fileList = Array.from(event.target.files || []);
    if (!fileList.length) return;

    const currentFiles = Array.isArray(formData.msdsFiles) ? formData.msdsFiles : [];
    if (currentFiles.length + fileList.length > MAX_MSDS_FILES) {
      toast.error(`Maximum ${MAX_MSDS_FILES} MSDS files allowed.`);
      event.target.value = '';
      return;
    }

    for (const file of fileList) {
      if (file.size > MAX_MSDS_FILE_SIZE_BYTES) {
        toast.error(`File "${file.name}" exceeds ${MAX_MSDS_FILE_SIZE_MB}MB.`);
        event.target.value = '';
        return;
      }
    }

    setUploadingMsds(true);
    try {
      const uploaded = [];
      for (const file of fileList) {
        const res = await uploadsApi.uploadFile(file, 'quote_document');
        const url = res?.data?.file?.url || res?.data?.file_url || res?.data?.url;
        const name = res?.data?.file?.original_filename || res?.data?.original_filename || file.name;
        const size = res?.data?.file?.size || file.size;
        if (url) {
          uploaded.push({ name, url, size });
        }
      }

      if (uploaded.length > 0) {
        setFormData((prev) => ({
          ...prev,
          msdsFiles: [...(prev.msdsFiles || []), ...uploaded]
        }));
        toast.success(`${uploaded.length} MSDS file(s) uploaded.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload MSDS file(s).');
    } finally {
      setUploadingMsds(false);
      event.target.value = '';
    }
  };

  const removeMsdsFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      msdsFiles: (prev.msdsFiles || []).filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const w = formData.totalWeight != null && String(formData.totalWeight).trim() !== '';
    const weightNum = parseFloat(formData.totalWeight, 10);
    if (!w || Number.isNaN(weightNum) || weightNum <= 0) {
      toast.error('Please enter a valid Total Weight (greater than 0).');
      return;
    }
    const rows = formData.dimensionsRows || [];
    const piecesNum = getTotalPiecesFromRows(rows);
    if (!piecesNum || Number.isNaN(piecesNum) || piecesNum <= 0) {
      toast.error('Please add at least 1 piece across the dimension rows.');
      return;
    }
    if (rows.length === 0) {
      toast.error('Please add at least one dimension set.');
      return;
    }
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i] || {};
      const rowPieces = parseInt(row.pieces, 10);
      const lenNum = parseFloat(row.length, 10);
      const widNum = parseFloat(row.width, 10);
      const heiNum = parseFloat(row.height, 10);
      if (
        Number.isNaN(rowPieces) || rowPieces <= 0 ||
        Number.isNaN(lenNum) || lenNum <= 0 ||
        Number.isNaN(widNum) || widNum <= 0 ||
        Number.isNaN(heiNum) || heiNum <= 0
      ) {
        toast.error(`Please complete dimension set ${i + 1} with pieces, length, width, and height greater than 0.`);
        return;
      }
    }
    onSubmit({
      ...formData,
      pieces: piecesNum
    });
  };

  const addDimensionRow = () => {
    setFormData((prev) => {
      const dimensionsRows = [...(prev.dimensionsRows || []), createEmptyDimensionRow()];
      return {
        ...prev,
        dimensionsRows,
        pieces: getTotalPiecesFromRows(dimensionsRows) || ''
      };
    });
  };

  const updateDimensionRow = (index, field, value) => {
    setFormData((prev) => {
      const rows = [...(prev.dimensionsRows || [])];
      if (!rows[index]) rows[index] = createEmptyDimensionRow();
      rows[index] = { ...rows[index], [field]: value };
      return {
        ...prev,
        dimensionsRows: rows,
        pieces: getTotalPiecesFromRows(rows) || ''
      };
    });
  };

  const removeDimensionRow = (index) => {
    setFormData((prev) => {
      const dimensionsRows = (prev.dimensionsRows || []).filter((_, i) => i !== index);
      const nextRows = dimensionsRows.length > 0 ? dimensionsRows : [createEmptyDimensionRow()];
      return {
        ...prev,
        dimensionsRows: nextRows,
        pieces: getTotalPiecesFromRows(nextRows) || ''
      };
    });
  };

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-6" noValidate>
      {/* Scope */}
      <div>
        <Label>Scope (Select all that apply)</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
          {scopeOptions.map((scope) => (
            <label key={scope} className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={formData.scope.includes(scope)}
                onChange={(e) => {
                  const newScopes = e.target.checked
                    ? [...formData.scope, scope]
                    : formData.scope.filter(s => s !== scope);
                  const next = { ...formData, scope: newScopes };
                  setFormData(next);
                  onFormDataChange?.({ scope: newScopes });
                  clearValidationError?.('scope');
                }}
                className="rounded"
              />
              <span className="text-sm">{scope}</span>
            </label>
          ))}
        </div>
        {validationErrors.scope && <p className="mt-1 text-sm text-red-600">{validationErrors.scope}</p>}
      </div>

      {/* Origin & Destination */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Route Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Origin Airport</Label>
            <AsyncSearchableSelect
              fetchOptions={async (q, lim) => {
                const res = await directoryApi.searchAirports(q, lim);
                return (res.data || []).map(mapAirportToOption);
              }}
              value={formData.origin}
              selectedLabel={airportLabels.origin}
              onChange={(value, item) => {
                setFormData({ ...formData, origin: value, originCountry: item?.country || '' });
                setAirportLabels((prev) => ({ ...prev, origin: item?.label || value }));
                clearValidationError?.('origin');
              }}
              placeholder="Select Airport..."
              className="w-full"
              error={Boolean(validationErrors.origin)}
            />
            {validationErrors.origin && <p className="mt-1 text-sm text-red-600">{validationErrors.origin}</p>}
          </div>
          <div>
            <Label>Destination Airport</Label>
            <AsyncSearchableSelect
              fetchOptions={async (q, lim) => {
                const res = await directoryApi.searchAirports(q, lim);
                return (res.data || []).map(mapAirportToOption);
              }}
              value={formData.destination}
              selectedLabel={airportLabels.destination}
              onChange={(value, item) => {
                setFormData({ ...formData, destination: value, destinationCountry: item?.country || '' });
                setAirportLabels((prev) => ({ ...prev, destination: item?.label || value }));
                clearValidationError?.('destination');
              }}
              placeholder="Select Airport..."
              className="w-full"
              error={Boolean(validationErrors.destination)}
            />
            {validationErrors.destination && <p className="mt-1 text-sm text-red-600">{validationErrors.destination}</p>}
          </div>
        </div>

        {requiresPickupAddress && (
          <div className="mt-4">
            <Label>Pick up Address</Label>
            <Input
              value={formData.pickupAddress}
              onChange={(e) => {
                setFormData({ ...formData, pickupAddress: e.target.value });
                clearValidationError?.('pickupAddress');
              }}
              placeholder="Complete pick up address"
            />
            {validationErrors.pickupAddress && <p className="mt-1 text-sm text-red-600">{validationErrors.pickupAddress}</p>}
          </div>
        )}

        {requiresDestinationAddress && (
          <div className="mt-4">
            <Label>Destination Address</Label>
            <Input
              value={formData.deliveryAddress}
              onChange={(e) => {
                setFormData({ ...formData, deliveryAddress: e.target.value });
                clearValidationError?.('deliveryAddress');
              }}
              placeholder="Complete destination address"
            />
            {validationErrors.deliveryAddress && <p className="mt-1 text-sm text-red-600">{validationErrors.deliveryAddress}</p>}
          </div>
        )}
      </div>

      {/* Cargo Details */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Cargo Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Commodity</Label>
            <SearchableSelect
              options={commodityOptions}
              value={formData.commodity}
              onChange={(value) => {
                setFormData({ ...formData, commodity: value || '' });
                if (value) clearValidationError?.('commodity');
              }}
              placeholder="Search or select commodity"
              allowOther={false}
              error={!!validationErrors.commodity}
            />
            {validationErrors.commodity && <p className="mt-1 text-sm text-red-600">{validationErrors.commodity}</p>}
          </div>
          <div>
            <Label>Cargo Type</Label>
            <SearchableSelect
              options={AIR_CARGO_TYPE_OPTIONS}
              value={formData.cargoType}
              onChange={(value) => setFormData({ ...formData, cargoType: value || 'General' })}
              placeholder="Search or select cargo type"
              allowOther={false}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <Label>Total Weight <span className="text-red-500">*</span></Label>
            <div className="flex space-x-2">
              <Input
                type="number"
                min="0.01"
                step="any"
                value={formData.totalWeight}
                onChange={(e) => setFormData({ ...formData, totalWeight: e.target.value })}
                placeholder="0"
                required
              />
              <SearchableSelect
                options={WEIGHT_UNIT_OPTIONS}
                className="w-24"
                value={formData.weightUnit}
                onChange={(value) => setFormData({ ...formData, weightUnit: value || 'kg' })}
                placeholder="Unit"
                allowOther={false}
              />
            </div>
          </div>
          <div>
            <Label>Number of Pieces <span className="text-red-500">*</span></Label>
            <Input
              type="number"
              min="1"
              value={formData.pieces}
              readOnly
              disabled
              placeholder="Calculated from rows"
              className="bg-gray-50 text-gray-600 cursor-not-allowed"
            />
          </div>
          <div>
            <Label>Service Level</Label>
            <SearchableSelect
              options={serviceLevelOptions}
              value={formData.serviceLevel}
              onChange={(value) => setFormData({ ...formData, serviceLevel: value || 'Standard' })}
              placeholder="Search or select service level"
              allowOther={false}
            />
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-xs">Dimensions per Piece <span className="text-red-500">*</span></Label>
          <div className="space-y-2 mt-1">
            {(formData.dimensionsRows || [createEmptyDimensionRow()]).map((row, index) => (
              <div key={index} className="rounded-md border border-gray-200 bg-white p-3 space-y-2">
                <div className="flex flex-wrap items-end gap-2">
                  <SearchableSelect
                    options={DIMENSION_UNIT_OPTIONS}
                    className="w-24 shrink-0"
                    value={row?.unit ?? 'cm'}
                    onChange={(value) => updateDimensionRow(index, 'unit', value || 'cm')}
                    placeholder="Unit"
                    allowOther={false}
                  />
                  <Input
                    type="number"
                    className="flex-1 min-w-[6rem]"
                    value={row?.pieces ?? ''}
                    onChange={(e) => updateDimensionRow(index, 'pieces', e.target.value)}
                    placeholder="No. of Pieces"
                  />
                  <Input
                    type="number"
                    className="flex-1 min-w-[4rem]"
                    value={row?.length ?? ''}
                    onChange={(e) => updateDimensionRow(index, 'length', e.target.value)}
                    placeholder="Length"
                  />
                  <Input
                    type="number"
                    className="flex-1 min-w-[4rem]"
                    value={row?.width ?? ''}
                    onChange={(e) => updateDimensionRow(index, 'width', e.target.value)}
                    placeholder="Width"
                  />
                  <Input
                    type="number"
                    className="flex-1 min-w-[4rem]"
                    value={row?.height ?? ''}
                    onChange={(e) => updateDimensionRow(index, 'height', e.target.value)}
                    placeholder="Height"
                  />
                  {(formData.dimensionsRows || []).length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDimensionRow(index)}
                      className="px-2 py-2 text-xs text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-4 text-xs">
                  <span className="text-gray-600 font-medium">Stackability</span>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name={`air-dim-stack-${index}`}
                      checked={row?.stackability === 'stackable'}
                      onChange={() => updateDimensionRow(index, 'stackability', 'stackable')}
                    />
                    Stackable
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name={`air-dim-stack-${index}`}
                      checked={row?.stackability === 'non_stackable'}
                      onChange={() => updateDimensionRow(index, 'stackability', 'non_stackable')}
                    />
                    Non-stackable
                  </label>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addDimensionRow}
              className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
            >
              + Add
            </button>
          </div>
        </div>
      </div>

      {/* Special Handling */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Special Handling</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {specialHandlingOptions.map((option) => (
            <label key={option} className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={formData.specialHandling.includes(option)}
                onChange={(e) => {
                  const newHandling = e.target.checked
                    ? [...formData.specialHandling, option]
                    : formData.specialHandling.filter(h => h !== option);
                  setFormData({ ...formData, specialHandling: newHandling });
                }}
                className="rounded"
              />
              <span className="text-sm">{option}</span>
            </label>
          ))}
        </div>

        {formData.specialHandling.includes('Temperature Controlled') && (
          <div className="mt-4">
            <Label htmlFor="temperatureRange">Temperature range required</Label>
            <Input
              id="temperatureRange"
              value={formData.temperatureRange ?? ''}
              onChange={(e) => setFormData({ ...formData, temperatureRange: e.target.value })}
              placeholder="e.g., 2°C to 8°C or -18°C to -15°C"
              className="mt-1 max-w-md"
            />
          </div>
        )}

        <div className="mt-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={formData.isHazardous}
              onChange={(e) => setFormData({ ...formData, isHazardous: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm font-medium">Dangerous Goods / Hazardous Material</span>
          </label>
        </div>

        {formData.isHazardous && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 p-4 bg-red-50 rounded-lg">
            <div>
              <Label>UN Number</Label>
              <Input
                value={formData.unNumber}
                onChange={(e) => setFormData({ ...formData, unNumber: e.target.value })}
                placeholder="e.g., UN1234"
                required={formData.isHazardous}
              />
            </div>
            <div>
              <Label>Hazard Class</Label>
              <SearchableSelect
                options={hazClassOptions}
                value={formData.hazClass}
                onChange={(value) => setFormData({ ...formData, hazClass: value || '' })}
                placeholder="Search or select hazard class"
                allowOther={false}
              />
            </div>
            <div className="md:col-span-2">
              <Label className="block mb-2">MSDS Documents (max {MAX_MSDS_FILES} files, {MAX_MSDS_FILE_SIZE_MB}MB each)</Label>
              <div className="flex items-center gap-3 flex-wrap">
                <Input
                  ref={msdsInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  multiple
                  className="hidden"
                  onChange={handleMsdsUpload}
                />
                <button
                  type="button"
                  onClick={() => msdsInputRef.current?.click()}
                  disabled={uploadingMsds || (formData.msdsFiles || []).length >= MAX_MSDS_FILES}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingMsds ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploadingMsds ? 'Uploading...' : ((formData.msdsFiles || []).length >= MAX_MSDS_FILES ? 'Max files reached' : 'Upload MSDS')}
                </button>
                <span className="text-xs text-gray-500">
                  Upload up to {MAX_MSDS_FILES} documents. Accepted: PDF, DOC, DOCX.
                </span>
                <span className="text-xs font-medium text-gray-700">
                  {(formData.msdsFiles || []).length} / {MAX_MSDS_FILES} files selected
                </span>
              </div>
              {(formData.msdsFiles || []).length > 0 && (
                <div className="mt-3 space-y-2">
                  {(formData.msdsFiles || []).map((file, index) => (
                    <div key={`${file.url || file.name}-${index}`} className="flex items-center justify-between gap-3 rounded-md border bg-white px-3 py-2">
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-blue-600 hover:underline truncate"
                      >
                        {file.name || `MSDS ${index + 1}`}
                      </a>
                      <button
                        type="button"
                        onClick={() => removeMsdsFile(index)}
                        className="text-gray-500 hover:text-red-600"
                        aria-label={`Remove ${file.name || `MSDS ${index + 1}`}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Insurance & Customs */}
      <div className="border-t pt-4">
        <h3 className="font-semibold text-lg mb-4">Insurance & Customs</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <div className="space-y-2">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.insuranceRequired}
                onChange={(e) => setFormData({ ...formData, insuranceRequired: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm font-medium">Insurance Required</span>
            </label>
            {formData.insuranceRequired && (
              <div>
                <Label className="sr-only">Insurance Value (USD)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.insuranceValue === '' || formData.insuranceValue == null ? '' : formData.insuranceValue}
                  onChange={(e) => setFormData({ ...formData, insuranceValue: e.target.value })}
                  placeholder="Insurance Value (USD)"
                  className="w-full"
                />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Incoterm *</Label>
            <SearchableSelect
              options={incotermOptions}
              value={formData.incoterm}
              onChange={(value) => { setFormData({ ...formData, incoterm: value || '' }); clearValidationError?.('incoterm'); }}
              placeholder="Search or select incoterm"
              allowOther={false}
              error={Boolean(validationErrors.incoterm)}
            />
            {validationErrors.incoterm && <p className="mt-1 text-sm text-red-600">{validationErrors.incoterm}</p>}
          </div>
        </div>
      </div>

    </form>
  );
};

export default AirFreightForm;
